import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PenTool, FileText, Layers } from "lucide-react";
import SignatureTrackingCard from "./SignatureTrackingCard";
import { Badge } from "@/components/ui/badge";

interface SignaturesTabProps {
  taskId: string;
  unaId: string;
}

export default function SignaturesTab({ taskId, unaId }: SignaturesTabProps) {
  const [signatureRequests, setSignatureRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [phases, setPhases] = useState<any[]>([]);

  useEffect(() => {
    loadSignatureRequests();
    loadPhases();

    // Set up real-time subscription
    const channel = supabase
      .channel(`task-signatures-${taskId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'signature_requests',
          filter: `task_id=eq.${taskId}`,
        },
        () => {
          loadSignatureRequests();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [taskId]);

  const loadSignatureRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('signature_requests')
        .select(`
          *,
          document:generated_documents!signature_requests_document_id_fkey(document_name, file_url)
        `)
        .eq('task_id', taskId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSignatureRequests(data || []);
    } catch (error) {
      console.error('Error loading signature requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPhases = async () => {
    try {
      const { data, error } = await supabase
        .from('task_phases')
        .select('*')
        .eq('task_id', taskId)
        .order('order_number');
      
      if (error) throw error;
      setPhases(data || []);
    } catch (error) {
      console.error('Error loading phases:', error);
    }
  };

  const getPhaseForSignature = (request: any) => {
    for (const phase of phases) {
      const sigReqs = phase.signature_requirements || [];
      const matchingReq = sigReqs.find((req: any) => 
        req.document_id === request.document_id ||
        req.signature_request_id === request.id
      );
      if (matchingReq) {
        return { phase, requirement: matchingReq };
      }
    }
    return null;
  };

  if (loading) {
    return <div className="text-center py-8">Loading signature requests...</div>;
  }

  if (signatureRequests.length === 0) {
    return (
      <Card className="p-8 text-center">
        <PenTool className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
        <h3 className="font-semibold mb-2">No Signature Requests Yet</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Once documents are finalized, you can request signatures from the Documents tab
        </p>
        <Button variant="outline" onClick={() => {
          // Navigate to documents tab
          const documentsTab = document.querySelector('[value="documents"]') as HTMLElement;
          documentsTab?.click();
        }}>
          <FileText className="w-4 h-4 mr-2" />
          View Documents
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Signature Requests ({signatureRequests.length})</h3>
      </div>

      <div className="space-y-6">
        {signatureRequests.map((request) => {
          const phaseInfo = getPhaseForSignature(request);
          
          return (
            <div key={request.id} className="space-y-2">
              {phaseInfo && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary/10 w-fit">
                  <Layers className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-medium text-primary">
                    Required for Phase {phaseInfo.phase.phase_number}: {phaseInfo.phase.phase_name}
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    {phaseInfo.requirement.status}
                  </Badge>
                </div>
              )}
              <SignatureTrackingCard signatureRequestId={request.id} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
